import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Button from "components/Button";
import DateForm from "components/DateForm";
import { TitleArea, FooterArea, ContentArea as BaseContentArea, FormRow, Board } from "components/LayoutParts";
import RadioButton from "components/RadioButton";
import TextareaForm from "components/TextareaForm";
import TextForm from "components/TextForm";
import UserModal, { UserModalConfig } from "components/UserModal";
import font from "constants/font";
import { HOURS, MINUTES } from "constants/time";
import { useCreateSystemNotification } from "pages/AtPort/api/useCreateSystemNotification";
import AtPortSelect from "pages/AtPort/component/AtPortSelect";
import {
  LESSEE_SYSTEM_NOTIFICATION_CATEGORY,
  LESSEE_SYSTEM_NOTIFICATION_CATEGORY_CHOICES,
  LesseeSystemNotificationCategory,
  SYSTEM_NOTIFICATION_PLACEHOLDER,
} from "pages/AtPort/constants/systemNotification";
import {
  CONFIRM_MODAL_CONFIG,
  FAILED_MODAL_CONFIG,
  INITIAL_MODAL_CONFIG,
  SUCCEED_MODAL_CONFIG,
} from "pages/AtPort/modalTemplate";

// パーツ定義
const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;
const Wrap = styled.div`
  margin-top: 10px;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  flex: 1;
`;
const ContentWrap = styled.div`
  gap: 16px;
`;
const ContentArea = styled(BaseContentArea)`
  display: flex;
`;
const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
`;
const Label = styled.div<{ top?: boolean }>`
  min-width: 200px;
  margin: 0 5px;
  padding-right: 18px;
  align-self: ${({ top }) => (top ? "flex-start" : "center")};
  margin-top: ${({ top }) => (top ? "14px" : "0")};
`;
const CurrentPageWrap = styled.p`
  font-size: ${font.size24};
  font-weight: bold;
  line-height: 40px;
`;
const DurationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const DurationWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const CalenderLabel = styled.div`
  font-size: ${font.size14};
`;

const CreateSystemNotification = () => {
  // フォームの値
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<Choice>();
  const [message, setMessage] = useState("");
  const [homeDisplay, setHomeDisplay] = useState(false);
  const [startAt, setStartAt] = useState("");
  const [startHourChoice, setStartHourChoice] = useState<Choice>();
  const [startMinuteChoice, setStartMinuteChoice] = useState<Choice>();
  const [endAt, setEndAt] = useState("");
  const [endHourChoice, setEndHourChoice] = useState<Choice>();
  const [endMinuteChoice, setEndMinuteChoice] = useState<Choice>();
  const [modalConfig, setModalConfig] = useState<UserModalConfig>(INITIAL_MODAL_CONFIG);
  const [isVisible, setIsVisible] = useState(false);

  const changeTitle = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const changeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value);

  const navigate = useNavigate();
  const { create } = useCreateSystemNotification();

  const startAtEmpty = !startAt && !startHourChoice && !startMinuteChoice;
  const endAtEmpty = !endAt && !endHourChoice && !endMinuteChoice;

  const formattedStartAt = dayjs(startAt + " " + startHourChoice?.value + ":" + startMinuteChoice?.value);
  const formattedEndAt = dayjs(endAt + " " + endHourChoice?.value + ":" + endMinuteChoice?.value);
  const isDateEmpty = startAtEmpty && endAtEmpty;
  const startAtValid = isDateEmpty ? true : formattedStartAt.isValid();
  const endAtValid = isDateEmpty ? true : formattedEndAt.isValid() && formattedStartAt.isBefore(formattedEndAt);
  const isErrorTitle = title === "";
  const isErrorMessage = message === "";
  const isErrorCategory = !category;
  const isValidDate = startAtValid && endAtValid;
  const canSubmit = !isErrorTitle && !isErrorMessage && !isErrorCategory && isValidDate;

  const isMaintain = category?.value === LESSEE_SYSTEM_NOTIFICATION_CATEGORY.MAINTENANCE;

  const onSubmit = useCallback(async () => {
    const date = isMaintain
      ? {
          endAt: endAtValid && !endAtEmpty ? formattedEndAt.toISOString() : undefined,
          startAt: startAtValid && !startAtEmpty ? formattedStartAt.toISOString() : undefined,
        }
      : undefined;
    const response = await create({
      content: message,
      category: category?.value as LesseeSystemNotificationCategory,
      homeDisplay,
      title,
      ...date,
    }).catch((e) => {
      setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
      setIsVisible(true);
      throw e;
    });

    if (response.status === 201) {
      setModalConfig(
        SUCCEED_MODAL_CONFIG({
          message: "送信しました",
          onClose: () => navigate(`/at_port/system_notification`),
        })
      );
      setIsVisible(true);
    }
  }, [
    endAtEmpty,
    startAtEmpty,
    isMaintain,
    create,
    category,
    message,
    navigate,
    title,
    setModalConfig,
    setIsVisible,
    formattedEndAt,
    formattedStartAt,
    homeDisplay,
    endAtValid,
    startAtValid,
  ]);

  const handleOpenSubmitModal = useCallback(() => {
    setModalConfig(
      CONFIRM_MODAL_CONFIG({
        negative: () => setIsVisible(false),
        positive: onSubmit,
      })
    );
    setIsVisible(true);
  }, [onSubmit, setModalConfig, setIsVisible]);

  return (
    <Board>
      <TitleArea>
        <CurrentPageWrap>お知らせを送信</CurrentPageWrap>
      </TitleArea>
      <Wrap>
        <ContentWrap>
          <ContentArea>
            <FormRow>
              <Label>カテゴリ</Label>
              <AtPortSelect
                choices={LESSEE_SYSTEM_NOTIFICATION_CATEGORY_CHOICES.map((choice) => ({
                  label: choice,
                  value: choice,
                }))}
                value={category}
                onChange={(v) => setCategory(v)}
                width="350px"
                placeholder="選択してください"
              />
            </FormRow>
            <FormRow>
              <Label>タイトル</Label>
              <TextForm
                type="text"
                width="832px"
                value={title}
                placeholder="例）システムトラブル発生のため、各種申請の受付を一時停止しております"
                required
                onChange={changeTitle}
                errorMessage="タイトルを入力してください"
                isError={isErrorTitle}
              />
            </FormRow>
            <FormRow>
              <Label top>本文</Label>
              <TextareaForm
                width="832px"
                minHeight={400}
                errorMessage="本文を入力してください"
                onChange={changeMessage}
                value={message}
                isError={isErrorMessage}
                placeholder={SYSTEM_NOTIFICATION_PLACEHOLDER}
              />
            </FormRow>
            {isMaintain && (
              <>
                <FormRow>
                  <Label top>期間</Label>
                  <DurationContainer>
                    <DurationWrap>
                      <CalenderLabel>開始</CalenderLabel>
                      <DateForm
                        value={startAt}
                        width="160px"
                        required
                        errorMessage="終了日は開始日より後にしてください"
                        onChange={(v) => setStartAt(v.target.value)}
                      />
                      <AtPortSelect
                        choices={HOURS.map((hour) => ({
                          label: hour,
                          value: hour,
                        }))}
                        onChange={(v) => setStartHourChoice(v)}
                        value={startHourChoice}
                      />
                      <div>時</div>
                      <AtPortSelect
                        choices={MINUTES.map((minute) => ({
                          label: minute,
                          value: minute,
                        }))}
                        onChange={(v) => setStartMinuteChoice(v)}
                        value={startMinuteChoice}
                      />
                      <div>分</div>
                    </DurationWrap>
                    <DurationWrap>
                      <CalenderLabel>終了</CalenderLabel>
                      <DateForm
                        value={endAt}
                        width="160px"
                        required
                        errorMessage="終了日は開始日より後にしてください"
                        onChange={(v) => setEndAt(v.target.value)}
                      />
                      <AtPortSelect
                        choices={HOURS.map((hour) => ({
                          label: hour,
                          value: hour,
                        }))}
                        onChange={(v) => setEndHourChoice(v)}
                        value={endHourChoice}
                      />
                      <div>時</div>
                      <AtPortSelect
                        choices={MINUTES.map((minute) => ({
                          label: minute,
                          value: minute,
                        }))}
                        onChange={(v) => setEndMinuteChoice(v)}
                        value={endMinuteChoice}
                      />
                      <div>分</div>
                    </DurationWrap>
                  </DurationContainer>
                </FormRow>
              </>
            )}
            <FormRow>
              <Label>ホーム画面に表示</Label>
              <RadioButton
                id="home_display_edit_btn"
                value={homeDisplay ? 1 : 0}
                choices={[
                  {
                    label: "表示する",
                    value: 1,
                  },
                  {
                    label: "表示しない",
                    value: 0,
                  },
                ]}
                onChange={(v) => setHomeDisplay(Number(v.target.value) === 1)}
              />
            </FormRow>
          </ContentArea>
        </ContentWrap>
        <FooterArea>
          <CancelWrap>
            <Button
              type="secondary"
              onClick={() => navigate("/at_port/system_notification")}
              label="キャンセル"
              width="160px"
            />
          </CancelWrap>
          <SaveWrap>
            <Button
              type="primary"
              onClick={handleOpenSubmitModal}
              label="お知らせを送信"
              disabled={!canSubmit}
              width="160px"
            />
          </SaveWrap>
        </FooterArea>
      </Wrap>
      <UserModal isVisible={isVisible} config={modalConfig} />
    </Board>
  );
};

export default CreateSystemNotification;
